// AR preview modal — wraps Google's <model-viewer> component with AR launch buttons
// for Android Scene Viewer + iOS AR Quick Look. Falls back to a rotatable 3D preview
// on desktop / unsupported browsers. Loaded via CDN (no bundler cost).
import { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Smartphone, Loader2, Info, RotateCw } from "lucide-react";

const MODEL_VIEWER_CDN = "https://cdn.jsdelivr.net/npm/@google/model-viewer@3.5.0/dist/model-viewer.min.js";

// Loads the <model-viewer> web component once per session.
function useModelViewer() {
  const [ready, setReady] = useState(!!window.customElements?.get("model-viewer"));
  useEffect(() => {
    if (ready) return;
    if (document.querySelector(`script[src="${MODEL_VIEWER_CDN}"]`)) {
      // Already loading — poll for definition
      const t = setInterval(() => {
        if (window.customElements?.get("model-viewer")) { setReady(true); clearInterval(t); }
      }, 200);
      return () => clearInterval(t);
    }
    const s = document.createElement("script");
    s.type = "module";
    s.src = MODEL_VIEWER_CDN;
    s.onload = () => setReady(true);
    document.head.appendChild(s);
  }, [ready]);
  return ready;
}

export default function PortalARModal({ open, onClose, glbUrl, designLabel, colourName, materialLabel, heightMm, totalM }) {
  const ready = useModelViewer();
  const [interacted, setInteracted] = useState(false);
  const mvRef = useRef(null);

  useEffect(() => {
    if (!open) setInteracted(false);
  }, [open]);

  const activateAR = () => {
    if (mvRef.current && typeof mvRef.current.activateAR === "function") {
      mvRef.current.activateAR();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-2xl p-0 overflow-hidden" data-testid="portal-ar-modal">
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-slate-200">
          <DialogTitle className="font-display text-lg flex items-center gap-2">
            <Smartphone size={18} className="text-cyan-600" /> See it at your place — AR preview
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {designLabel} · {materialLabel}{colourName ? ` · ${colourName}` : ""} · {(totalM || 0).toFixed(1)} m × {heightMm} mm
          </DialogDescription>
        </DialogHeader>

        <div className="relative bg-gradient-to-b from-slate-900 to-slate-700 aspect-video">
          {!ready ? (
            <div className="absolute inset-0 flex items-center justify-center text-white text-sm gap-2">
              <Loader2 size={16} className="animate-spin" /> Loading 3D viewer…
            </div>
          ) : (
            <model-viewer
              ref={mvRef}
              src={glbUrl}
              alt={`3D fence — ${designLabel}`}
              ar
              ar-modes="webxr scene-viewer quick-look"
              ar-scale="fixed"
              ar-placement="floor"
              camera-controls
              touch-action="pan-y"
              auto-rotate
              auto-rotate-delay="4000"
              shadow-intensity="0.9"
              exposure="0.95"
              environment-image="neutral"
              onLoad={() => setInteracted(true)}
              style={{ width: "100%", height: "100%", background: "transparent" }}
              data-testid="portal-ar-model-viewer"
            >
              <div slot="progress-bar" className="absolute inset-x-0 top-0 h-1 bg-cyan-500/20">
                <div className="h-full bg-cyan-400 animate-pulse w-1/3" />
              </div>
              <button slot="ar-button" type="button"
                className="absolute bottom-4 right-4 rounded-full bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-medium px-4 py-2.5 shadow-lg flex items-center gap-2"
                data-testid="portal-ar-launch-btn">
                <Smartphone size={14} /> View in your space
              </button>
            </model-viewer>
          )}
          <div className="absolute top-3 left-3 rounded-sm bg-white/85 text-[10px] text-slate-700 px-2 py-1 font-mono flex items-center gap-1">
            <RotateCw size={9} /> Drag to rotate · Pinch to zoom
          </div>
        </div>

        <div className="px-5 py-4 space-y-3 bg-slate-50 border-t border-slate-200">
          <div className="flex flex-wrap gap-2 items-center">
            <Badge variant="outline" className="rounded-sm border-cyan-300 bg-cyan-50 text-cyan-700 text-[10px] uppercase tracking-wider">Android</Badge>
            <span className="text-xs text-slate-600">Tap "View in your space" — opens Scene Viewer to walk around your driveway with the fence pinned</span>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Badge variant="outline" className="rounded-sm border-slate-300 bg-slate-100 text-slate-700 text-[10px] uppercase tracking-wider">iPhone / iPad</Badge>
            <span className="text-xs text-slate-600">Same button — opens AR Quick Look for full room-scale AR</span>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Badge variant="outline" className="rounded-sm border-amber-300 bg-amber-50 text-amber-700 text-[10px] uppercase tracking-wider">Desktop</Badge>
            <span className="text-xs text-slate-600">Drag / pinch above to rotate the fence — mobile AR needs a phone or tablet</span>
          </div>

          <div className="pt-2 border-t border-slate-200 flex flex-col sm:flex-row gap-2">
            <Button onClick={activateAR} disabled={!ready} className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="portal-ar-launch-cta">
              <Smartphone size={13} className="mr-1.5" /> Launch AR
            </Button>
            <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="portal-ar-close-btn">Close</Button>
            <div className="text-[11px] text-slate-500 flex items-center gap-1 sm:ml-auto"><Info size={11} /> AR works on newer Android and iOS devices</div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
